
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { signOut } from "@/lib/auth";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { UserCircle, LogOut } from "lucide-react";

const AuthStatus = () => {
  const { user, profile, loading } = useAuth();
  const { toast } = useToast();

  const handleSignOut = async (e: React.MouseEvent) => {
    // Prevent any default behavior
    e.preventDefault();
    e.stopPropagation();
    
    try {
      toast({
        title: "Välja logimine...",
      });
      
      // Call the signOut function
      const { error } = await signOut();
      
      if (error) {
        throw error;
      }
      
      // If we reach here, signOut was successful
      toast({
        title: "Edukalt välja logitud",
        description: "Teid suunatakse avalehele",
      });
      
      // Force page refresh after a short delay to ensure the auth state is cleared properly
      setTimeout(() => {
        window.location.reload();
      }, 800);
      
    } catch (error: any) {
      console.error("Sign out error:", error);
      toast({
        title: "Viga väljalogimisel",
        description: "Proovi värskendada lehte",
        variant: "destructive",
      });
      
      // Force refresh on error as a last resort
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    }
  };

  if (loading) {
    return <div className="animate-pulse h-8 w-24 bg-gray-200 rounded"></div>;
  }

  if (user) {
    return (
      <div className="flex items-center gap-2">
        <Link 
          to="/profile" 
          className="flex items-center gap-1 text-sm font-medium hover:text-kaija-accent transition-colors"
        >
          <UserCircle className="h-4 w-4" />
          <span className="hidden md:inline">{profile?.full_name || user.email}</span>
        </Link>
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={handleSignOut}
          className="text-gray-500 hover:text-gray-700"
        >
          <LogOut className="h-4 w-4" />
          <span className="sr-only md:not-sr-only md:ml-1">Logi välja</span>
        </Button>
      </div>
    );
  }

  return (
    <Link to="/auth">
      <Button variant="outline" size="sm">
        Logi sisse
      </Button>
    </Link>
  );
};

export default AuthStatus;
